import { mergeTimeRanges, removeTimelineTime, type TimeRange } from '@waka/compat/timeline-ripple';
import { isLinkableTrack } from '../../utils/find-linked-items';
import type { SilenceSegment } from '../../utils/silence-detection';
import type { EditorState } from '../types';

export interface RemoveSilenceParams {
  state: EditorState;
  itemId: string;
  /** Clip-relative timeline seconds (source seconds must first be divided by playbackRate). */
  silenceSegments: SilenceSegment[];
}

export type SilenceRemoval = Omit<RemoveSilenceParams, 'state'>;

/** The linked main tracks share one clock. Music/effect/layer tracks retain their own clocks. */
export function silenceTrackScope(state: EditorState, itemId: string): string[] {
  const track = state.undoableState.tracks.find((entry) => entry.items.includes(itemId));
  if (!track || track.locked || track.metadata?.kind === 'bgm') return [];
  return state.isLinkingEnabled && isLinkableTrack(track)
    ? state.undoableState.tracks
        .filter((entry) => !entry.locked && isLinkableTrack(entry) && entry.metadata?.kind !== 'bgm')
        .map((entry) => entry.id)
    : [track.id];
}

export function silenceRemovalRanges(state: EditorState, removals: readonly SilenceRemoval[]) {
  const byTrack = new Map<string, TimeRange[]>();
  const { fps, items } = state.undoableState;
  if (!Number.isFinite(fps) || fps <= 0) return byTrack;
  for (const { itemId, silenceSegments } of removals) {
    const item = items[itemId];
    if (!item || (item.type !== 'video' && item.type !== 'audio')) continue;
    const ranges = silenceSegments.flatMap((segment) => {
      if (!Number.isFinite(segment.startInSeconds) || !Number.isFinite(segment.endInSeconds)) return [];
      // Keep the frames touching speech; never expand detection into adjacent sound.
      const start = Math.max(0, Math.ceil(segment.startInSeconds * fps - 1e-7));
      const end = Math.min(item.durationInFrames, Math.floor(segment.endInSeconds * fps + 1e-7));
      return end > start ? [{ start: item.from + start, end: item.from + end }] : [];
    });
    for (const trackId of silenceTrackScope(state, itemId)) {
      byTrack.set(trackId, [...(byTrack.get(trackId) ?? []), ...ranges]);
    }
  }
  for (const [trackId, ranges] of byTrack) byTrack.set(trackId, mergeTimeRanges(ranges));
  return byTrack;
}

/** Union first, then split/trim/ripple with the canonical rate-aware operation. Caller commits once. */
export function removeSilenceSelection(state: EditorState, removals: readonly SilenceRemoval[]): EditorState {
  const byTrack = silenceRemovalRanges(state, removals);
  let next = state;
  for (const [trackId, ranges] of byTrack) {
    const result = removeTimelineTime(next, [trackId], ranges);
    if (!result.ok) return state; // Atomic failure, including invalid or locked descendants.
    next = result.state;
  }
  return next;
}

export function removeSilence({ state, itemId, silenceSegments }: RemoveSilenceParams): EditorState {
  return removeSilenceSelection(state, [{ itemId, silenceSegments }]);
}
